import { useEffect, useRef } from 'react';

/**
 * Standard view ↔ viewmodel handshake used across the app.
 *
 * Per project convention, every viewmodel exposes an `initialize` function.
 * The view calls `onViewModelReady(initialize)` once on mount; the viewmodel
 * is expected to `setBusy(true)` while it loads state and `setBusy(false)`
 * when ready.
 */
export function onViewModelReady(initialize: () => void | Promise<void>): void {
  // Caller invokes this inside a useEffect — the wrapper simply ensures any
  // returned Promise is consumed without leaking unhandled rejections.
  void initialize();
}

/**
 * Convenience hook: runs `initialize` exactly once when the component mounts.
 * Equivalent to `useEffect(() => { onViewModelReady(initialize); }, [])` but
 * with a stable ref so a fresh `initialize` closure on each render doesn't
 * retrigger.
 */
export function useViewModelReady(initialize: () => void | Promise<void>): void {
  const initRef = useRef(initialize);
  initRef.current = initialize;

  useEffect(() => {
    onViewModelReady(() => initRef.current());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
